/**
 * Quantity-mode (MDOT-24700) submit serializer: maps editor rows to the save-endpoint
 * `ReviewTicker` shape, layering the quantity fields the shared weight-mode mapper
 * (`covertInstrumentEditorToReviewTicker`) deliberately drops.
 *
 * Contract (decided 2026-08-28):
 * - every non-cash row carries `quantity`, `price` (the unit market price) and
 *   `marketValue` (as loaded, as Cold War sized it, or as `./derive` repriced it) on top of
 *   the weight-mode fields;
 * - a row held by value (MDOT-25244) — a nested portfolio, a proxy, an index, flagged or of
 *   that nature (`./heldByValue`) — goes out with no `quantity` and no `price` at all, on its
 *   `marketValue` and weight alone: the save refuses a share count on it;
 * - the structural cash row IS submitted, with its derived `marketValue`
 *   (`AUM − Σ rowInvestedMarketValue`, invariant I1) and weight but no quantity/price — cash
 *   has no unit price or share count.
 *
 * Import rule: like the rest of `quantity/`, this module may import only `../builder`,
 * `../const` and its own siblings.
 */
import type { ReviewTicker } from "@eu.mdotm.api/sphere";
import { covertInstrumentEditorToReviewTicker } from "../builder";
import type { Instrument } from "../const";
import { isHeldByValueByNature } from "./heldByValue";

export function convertQuantityCompositionToReviewTicker(composition: Instrument[]): ReviewTicker[] {
	return covertInstrumentEditorToReviewTicker(composition).map((ticker, i) =>
		withQuantityFields(ticker, composition[i]),
	);
}

/** `ticker` with the quantity fields `row` goes out with, by the contract above. */
export function withQuantityFields(ticker: ReviewTicker, row: Instrument): ReviewTicker {
	if (row.isCash) {
		// TODO(MDOT-24700): when the cash row was frontend-injected (no real cash instrument in
		// the composition) it has no tickerName, so this entry submits with `ticker: undefined`.
		// Revisit once the backend save contract lands: if it needs a concrete cash ticker
		// identity, decide it there.
		return { ...ticker, marketValue: row.marketValue };
	}
	if (row.heldByValue || isHeldByValueByNature(row)) {
		return { ...ticker, marketValue: row.marketValue };
	}
	return { ...ticker, quantity: row.quantity, price: row.price, marketValue: row.marketValue };
}
